import { extractClientMatterNumbers } from '@repo/utils/clientMatterNumbers'
import { formatCurrency, formatThousands, roundNumber } from '@repo/utils/numbers'
import { formatDate } from '@repo/utils/time'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { storeWrapper } from '../storeWrapper'
import { throttle, debounce } from '@repo/utils/debounce'
import { useLookupsStore } from '@repo/store/useLookupsStore'

const initialState = {
  fetchedCustomDataPages: [],
  fetchedCustomDataPageFields: [],
  customDataPages: {},
  customDataPagesByWorktype: {},
  customDataPageFields: {},
  questionnaireTypes: [],
  listValues: {}
}

export const useCustomDataPageStore = storeWrapper('useCustomDataPageStore', (set, get) => ({
  ...initialState,
  fetchCustomDataPages: async ({ workflowTypeId }) => {
    throttle({
      id: `fetchCustomDataPages-${workflowTypeId || 'null'}`,
      callback: async () => {
        const { rows } = await ospreyAPI({
          service: 'customdata',
          endpoint: 'CustomDataPage/GetAllSearch',
          data: {
            WorkflowTypeId: workflowTypeId,
            StartAtIndex: 0,
            PageSize: 100,
            OrderByColumns: [
              {
                ColumnName: 'Description',
                Direction: 0
              }
            ]
          }
        })

        const { customDataPagesByWorktype } = get()
        customDataPagesByWorktype[workflowTypeId] = rows
        set({ customDataPagesByWorktype })
      }
    })
  },
  fetchCustomDataPagesWithFields: async ({ workflowTypeId }) => {
    return new Promise((resolve, reject) => {
      throttle({
        id: `fetchCustomDataPages-${workflowTypeId || 'null'}`,
        callback: async () => {
          try {
            let { rows } = await ospreyAPI({
              service: 'customdata',
              endpoint: 'CustomDataPage/GetAllSearch',
              data: {
                WorkflowTypeId: workflowTypeId,
                StartAtIndex: 0,
                PageSize: 250
              },
              suppress404: true
            })

            const customDataPageIds = rows.map((row) => `customDataPageIds=${row.customDataPageId}`).join('&')
            const fieldsForAllPages = await get().fetchMultipleCustomDataPageFields({ customDataPageIds })

            rows = rows
              .map((row) => {
                const fields = fieldsForAllPages.filter((field) => field.customDataPageId === row.customDataPageId)
                if (!fields?.length) return null

                return {
                  ...row,
                  fields
                }
              })
              .filter(Boolean)
              .sort((a, b) => {
                const aDescription = a.description.toLowerCase()
                const bDescription = b.description.toLowerCase()

                if (aDescription < bDescription) {
                  return -1
                }
                if (aDescription > bDescription) {
                  return 1
                }
                return 0
              })

            const { customDataPagesByWorktype } = get()
            customDataPagesByWorktype[workflowTypeId] = rows
            set({ customDataPagesByWorktype: { ...customDataPagesByWorktype } })
            resolve(rows)
          } catch (err) {
            reject(err)
          }
        }
      })
    })
  },
  fetchMultipleCustomDataPageFields: async ({ customDataPageIds }) => {
    let fetchedCustomDataPageFields = await ospreyAPI({
      service: 'customdata',
      endpoint: `CustomDataPageField/GetExtendedByCustomDataPageIds?${customDataPageIds}`,
      suppress404: true
    })

    fetchedCustomDataPageFields = fetchedCustomDataPageFields.map((field) => {
      if (!field.fieldId && field.customDataFieldId) field.fieldId = field.customDataFieldId
      if (!field.customDataFieldId && field.fieldId) field.customDataFieldId = field.fieldId
      field.fieldType = field.type

      return field
    })

    return fetchedCustomDataPageFields
  },
  fetchCustomDataPageById: async ({ pageId }) => {
    const customDataPage = await ospreyAPI({
      service: 'customdata',
      endpoint: 'CustomDataPage/Get',
      data: {
        id: pageId
      }
    })

    const customDataPages = get().customDataPages
    set({ customDataPages: { ...customDataPages, [pageId]: customDataPage } })
    return customDataPage
  },
  fetchSearchCustomDataPages: async ({ term = '', startAtIndex = 0, pageSize = 20 }) => {
    let fetchedCustomDataPages = await ospreyAPI({
      method: 'GET',
      service: 'customdata',
      endpoint: 'CustomDataPage/GetAllByDescription',
      data: {
        Description: term,
        StartAtIndex: startAtIndex,
        PageSize: pageSize,
        OrderByColumns: [
          {
            ColumnName: 'Description',
            Direction: 0
          }
        ]
      }
    })

    if (fetchedCustomDataPages.rows) {
      fetchedCustomDataPages.rows = fetchedCustomDataPages.rows.map((row) => {
        return {
          ...row,
          label: row.description,
          value: row.customDataPageId
        }
      })
      set({ fetchedCustomDataPages })
    }
    return fetchedCustomDataPages
  },
  fetchSearchCustomDataPageFields: async ({ customDataPageId }) => {
    let fetchedCustomDataPageFields = await ospreyAPI({
      service: 'customdata',
      endpoint: 'CustomDataPageField/GetExtendedByCustomDataPageId',
      data: {
        customDataPageId
      }
    })

    const fields = {}
    fetchedCustomDataPageFields?.forEach((field) => {
      const fieldId = field.fieldId || field.customDataFieldId
      field.fieldType = field.type
      fields[`${field.customDataPageId}-${fieldId}`] = field
    })

    set({ fetchedCustomDataPageFields, customDataPageFields: fields })
    return fetchedCustomDataPageFields
  },
  fetchCustomDataPageFieldValues: async ({ pageId, clientNo, matterNo, fields }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchCustomDataPageFieldValues-${pageId}-${clientNo}-${matterNo || 'null'}`,
        callback: async () => {
          let customDataPage = await ospreyAPI({
            service: 'customdata',
            endpoint: 'CustomDataPageFieldValueSet/Get',
            data: {
              customDataPageId: pageId,
              clientNo,
              matterNo
            }
          })

          if (!customDataPage || !customDataPage.customDataPageId) {
            customDataPage = {
              clientNo,
              matterNo,
              customDataPageId: pageId,
              customDataPageFieldValues: fields.map(({ fieldId }) => ({ fieldId, value: null }))
            }
          }

          customDataPage.customDataPageFieldValues = customDataPage?.customDataPageFieldValues?.map((field) => {
            if (field.value === null || field.value === undefined) {
              const defaultValue = get().getDefaultValueForCustomDataField({
                field: fields.find((f) => f.fieldId === field.fieldId)
              })

              if (defaultValue) {
                return { ...field, value: defaultValue }
              }
            }

            return field
          })

          const customDataPages = get().customDataPages
          set({ customDataPages: { ...customDataPages, [pageId]: customDataPage } })
          resolve(customDataPage)
        }
      })
    })
  },
  getDefaultValueForCustomDataField: ({ field }) => {
    const { defaultValue, fieldType, customDataFieldListId } = field || {}

    if (defaultValue && fieldType === 'Date') {
      // convert date from dd/mm/yyyy to yyyy-mm-dd
      const formattedDate = defaultValue.split('/').reverse().join('-')
      return formattedDate
    }

    if (defaultValue && (fieldType === 'Integer' || fieldType === 'Decimal' || fieldType === 'Money')) {
      return Number(defaultValue)
    }

    if (fieldType === 'List' && customDataFieldListId) {
      const list = get().listValues[customDataFieldListId] || {}

      if (list?.defaultPositionIndex || list?.defaultPositionIndex === 0) {
        const defaultListItem = list.customDataListItemValues.find(
          (listItem) => listItem.positionIndex === list.defaultPositionIndex
        )
        if (defaultListItem) return defaultListItem.value
      }
    }

    return defaultValue || null
  },
  fetchCustomDataPageFieldValuesByPageIdsAndClientMatterNo: async ({ clientNo, matterNo, customDataPageIds }) => {
    // send request
    const response = await ospreyAPI({
      service: 'customdata',
      endpoint: 'CustomDataPageFieldValueSet/GetAllByPageIdsAndClientNoAndMatterNo',
      method: 'POST',
      data: {
        clientNo,
        matterNo,
        customDataPageIds
      }
    })

    return response
  },
  updateCustomDataPageFieldValues: async ({ valueArray }) => {
    // send request
    const response = await ospreyAPI({
      service: 'customdata',
      endpoint: 'CustomDataPageFieldValueSet/Update',
      method: 'PUT',
      data: valueArray
    })

    return response
  },
  fetchQuestionnaireTypes: async () => {
    const questionnaireTypes = await ospreyAPI({
      service: 'customdata',
      endpoint: 'QuestionnaireType/GetAll'
    })

    set({ questionnaireTypes })
    return questionnaireTypes
  },
  fetchQuestionnairesByTypeIdAndWorktype: async ({ questionnaireTypeId, workTypeId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchQuestionnairesByTypeIdAndWorktype-${questionnaireTypeId}-${workTypeId || 'null'}`,
        callback: async () => {
          const questionnaires = await ospreyAPI({
            service: 'customdata',
            endpoint: 'Questionnaire/GetAllByQuestionnaireTypeIdAndWorkTypeId',
            data: {
              questionnaireTypeId,
              workTypeId
            }
          })

          resolve(questionnaires)
        }
      })
    })
  },
  fetchQuestionnairesByWorkTypeIdAndClientType: async ({ clientType, workTypeId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchQuestionnairesByWorkTypeIdAndClientType-${clientType}-${workTypeId || 'null'}`,
        callback: async () => {
          const questionnaires = await ospreyAPI({
            service: 'customdata',
            endpoint: 'Questionnaire/GetAllExtendedByWorkTypeIdAndClientType',
            data: {
              ClientType: clientType,
              WorkTypeId: workTypeId,
              startAtIndex: 0,
              pageSize: 100
            }
          })

          resolve(questionnaires)
        }
      })
    })
  },
  fetchQuestionnaireFieldsByQuestionnaireId: async ({ questionnaireId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchQuestionnaireFieldsByQuestionnaireId-${questionnaireId}`,
        callback: async () => {
          const questionnaireFields = await ospreyAPI({
            service: 'customdata',
            endpoint: 'QuestionnaireField/GetAllExtendedByQuestionnaireId',
            data: {
              questionnaireId
            }
          })

          resolve(questionnaireFields)
        }
      })
    })
  },
  fetchQuestionnaireFieldValues: async ({ questionnaireId, clientNo, matterNo = null }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchQuestionnaireFieldValues-${questionnaireId}-${clientNo}-${matterNo || 'null'}`,
        callback: async () => {
          const questionnaireFields = await ospreyAPI({
            service: 'customdata',
            endpoint: 'QuestionnaireField/GetQuestionnaireFieldValueSet',
            data: {
              questionnaireId,
              clientNo,
              matterNo
            }
          })

          resolve(questionnaireFields)
        }
      })
    })
  },
  fetchListValues: async ({ customDataListIds }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchListValues`,
        callback: async () => {
          const filteredCustomDataListIds = customDataListIds
            .filter((id, index, arr) => arr.indexOf(id) === index) // remove duplicates
            .join('&customDataListIds=')

          if (!filteredCustomDataListIds) {
            // if there's nothing left to fetch, we can just return
            resolve()
            return
          }

          // note, in the below we're appending the queryParams to the endpoint to avoid axios url encoding them
          const newLists = await ospreyAPI({
            service: 'customdata',
            endpoint: `CustomDataList/GetAllExtendedByCustomDataListIds?customDataListIds=${filteredCustomDataListIds}`
          })

          const listValuesById = {}

          newLists.forEach((list) => {
            // make sure the list values are in the correct order
            list.customDataListItemValues = list.customDataListItemValues.sort((a, b) => {
              if (a.positionIndex < b.positionIndex) {
                return -1
              }
              if (a.positionIndex > b.positionIndex) {
                return 1
              }
              return 0
            })

            // add the sorted list to the listValuesById object
            listValuesById[list.customDataListId] = list
          })

          set({ listValues: { ...get().listValues, ...listValuesById } })
          resolve()
        }
      })
    })
  },
  fetchMatterExtensions: async ({ clientMatterNo, workTypeId, includeValues = false }) => {
    const defaultResponse = null

    if (!workTypeId || (includeValues && !clientMatterNo)) {
      return defaultResponse
    }

    const { clientNo, matterNo } = extractClientMatterNumbers({ clientMatterNo }) || {}

    if (includeValues && (!clientNo || (!matterNo && matterNo !== 0))) {
      return defaultResponse
    }

    return new Promise((resolve) => {
      debounce({
        id: `fetchMatterExtensions-${clientNo}-${matterNo}`,
        delay: 500,
        callback: async () => {
          const {
            fetchQuestionnairesByTypeIdAndWorktype,
            fetchQuestionnaireFieldsByQuestionnaireId,
            fetchQuestionnaireFieldValues
          } = get()

          const questionnaireTypeId = 7 // this is the id for matter extensions

          let questionnaires = null

          // start by getting the questionnaires
          questionnaires = await fetchQuestionnairesByTypeIdAndWorktype({
            questionnaireTypeId,
            workTypeId
          })

          // now get the fields for each questionnaire
          // using an old fashioned loop as it supports async/await unlike forEach
          for (let i = 0; i < questionnaires.length; i++) {
            const questionnaireId = questionnaires[i].questionnaireId

            const questionnaireFields = await fetchQuestionnaireFieldsByQuestionnaireId({
              questionnaireId
            })

            questionnaireFields.forEach((field) => {
              const { pageId, dataType, maximumValue, minimumValue } = field

              // ensure data matches customDataField format
              field.fieldType = dataType
              field.customDataPageId = pageId
              field.maxValue = maximumValue
              field.minValue = minimumValue
            })

            questionnaires[i].fields = questionnaireFields
          }

          if (!questionnaires?.length) {
            resolve(defaultResponse)
            return
          }

          if (!includeValues) {
            resolve(questionnaires)
            return
          }

          // now get values for each questionnaire. We don't cache these so they are always fresh
          // using an old fashioned loop as it supports async/await unlike forEach
          for (let i = 0; i < questionnaires.length; i++) {
            const questionnaireId = questionnaires[i].questionnaireId

            const questionnaireValues = await fetchQuestionnaireFieldValues({
              questionnaireId,
              clientNo,
              matterNo
            })

            questionnaires[i].values = questionnaireValues?.questionnaireFieldValues
          }

          resolve(questionnaires)
        }
      })
    })
  },
  fetchClientExtensions: async ({ clientNo, clientType, includeValues = false }) => {
    const defaultResponse = null

    if (includeValues && (!clientNo || !clientType)) {
      return defaultResponse
    }

    return new Promise((resolve) => {
      debounce({
        id: `fetchClientExtensions-${clientNo}`,
        delay: 500,
        callback: async () => {
          const {
            fetchQuestionnairesByTypeIdAndWorktype,
            fetchQuestionnaireFieldsByQuestionnaireId,
            fetchQuestionnaireFieldValues
          } = get()

          let questionnaires = null

          const questionnaireTypeId = clientType === 'I' ? 6 : 8 // 6 is the id for client extensions (individual) and 8 is the id for client extensions (company)
          const workTypeId = 'GLOBAL' // this is the id for global questionnaires

          // start by getting the questionnaires
          questionnaires = await fetchQuestionnairesByTypeIdAndWorktype({
            questionnaireTypeId,
            workTypeId
          })

          // now get the fields for each questionnaire
          // using an old fashioned loop as it supports async/await unlike forEach
          for (let i = 0; i < questionnaires.length; i++) {
            const questionnaireId = questionnaires[i].questionnaireId

            const questionnaireFields = await fetchQuestionnaireFieldsByQuestionnaireId({
              questionnaireId
            })

            questionnaireFields.forEach((field) => {
              const { pageId, dataType, maximumValue, minimumValue } = field

              // ensure data matches customDataField format
              field.fieldType = dataType
              field.customDataPageId = pageId
              field.maxValue = maximumValue
              field.minValue = minimumValue
            })

            questionnaires[i].fields = questionnaireFields
          }

          if (!questionnaires?.length) {
            resolve(defaultResponse)
            return
          }

          if (!includeValues) {
            resolve(questionnaires)
            return
          }

          // now get values for each questionnaire. We don't cache these so they are always fresh
          // using an old fashioned loop as it supports async/await unlike forEach
          for (let i = 0; i < questionnaires.length; i++) {
            const questionnaireId = questionnaires[i].questionnaireId

            const questionnaireValues = await fetchQuestionnaireFieldValues({
              questionnaireId,
              clientNo
            })

            questionnaires[i].values = questionnaireValues?.questionnaireFieldValues
          }

          resolve(questionnaires)
        }
      })
    })
  },
  getRenderedValueOfCustomDataField: async ({ fieldType, value, addressSeparator = '\n' }) => {
    const valueIsSet = Boolean(value) || value === 0 || value === false
    const valueIsString = value && typeof value === 'string'

    // empty addresses can have a value of '||||' as the pipe character is used to separate the address lines
    const isEmptyAddress = fieldType === 'Address' && valueIsString && value.split('|').join('').trim() === ''

    if (!valueIsSet || isEmptyAddress) {
      return '-'
    }

    const { fetchContactById, fetchOrganisationById, expertTypesById, expertAreasById } = useLookupsStore.getState()

    switch (fieldType) {
      case 'Address':
        return value.split('|').filter(Boolean).join(addressSeparator)
      case 'Contact':
        const contact = await fetchContactById({ id: value })
        if (!contact) return '-'
        const {
          title,
          forename,
          surname,
          email,
          phoneNo,
          mobileNo,
          faxNo,
          expertTypeId,
          expertiseAreaId,
          websiteUrl,
          house,
          area,
          postalTown,
          county,
          postcode
        } = contact
        const contactName = [title, forename, surname].filter(Boolean).join(' ').trim()
        const emailAddress = email ? `E: ${email}` : null
        const phone = phoneNo ? `T: ${phoneNo}` : null
        const mobile = mobileNo ? `M: ${mobileNo}` : null
        const fax = faxNo ? `F: ${faxNo}` : null
        const website = websiteUrl ? `W: ${websiteUrl}` : null
        const address = [house, area, postalTown, county, postcode].filter(Boolean).join('\n')
        const expertType = expertTypesById?.[expertTypeId]?.description || null
        const expertiseArea = expertAreasById?.[expertiseAreaId]?.description || null

        const contactOrganisation = contact?.organisationId
          ? await fetchOrganisationById({ id: contact?.organisationId })
          : null
        const contactValue = [
          contactName,
          expertType,
          expertiseArea,
          contactOrganisation?.name || null,
          ' ', // force a line break
          emailAddress,
          phone,
          mobile,
          fax,
          website,
          address ? ' ' : '', // force a line break
          address
        ]
          .filter(Boolean)
          .join('\n')
          .trim()

        return contactValue
      case 'Date':
        return formatDate(value, 'EEE dd MMM yyyy')
      case 'Decimal':
        return formatThousands(value)
      case 'Money':
        return formatCurrency(value)
      case 'Org':
        const organisation = await fetchOrganisationById({ id: value })
        if (!organisation) return '-'

        const {
          name: orgName,
          email: orgEmail,
          phoneNo: orgPhoneNo,
          dxNo,
          faxNo: orgFaxNo,
          expertTypeId: orgExpertTypeId,
          websiteUrl: orgWebsiteUrl,
          house: orgHouse,
          area: orgArea,
          postalTown: orgPostalTown,
          county: orgCounty,
          postcode: orgPostcode
        } = organisation
        const orgEmailAddress = orgEmail ? `E: ${orgEmail}` : null
        const orgPhone = orgPhoneNo ? `T: ${orgPhoneNo}` : null
        const orgDx = dxNo ? `DX: ${dxNo}` : null
        const orgFax = orgFaxNo ? `F: ${orgFaxNo}` : null
        const orgWebsite = orgWebsiteUrl ? `W: ${orgWebsiteUrl}` : null
        const orgAddress = [orgHouse, orgArea, orgPostalTown, orgCounty, orgPostcode].filter(Boolean).join('\n')
        const orgExpertType = expertTypesById?.[orgExpertTypeId]?.description || null

        const organisationValue = [
          orgName,
          orgExpertType,
          ' ', // force a line break
          orgEmailAddress,
          orgPhone,
          orgDx,
          orgFax,
          orgWebsite,
          orgAddress ? ' ' : '', // force a line break
          orgAddress
        ]
          .filter(Boolean)
          .join('\n')
          .trim()

        return organisationValue
      case 'Email':
      case 'URL':
        return value.toLowerCase()
      default:
        return value
    }
  },
  applyCustomDataFieldDefaultsAndFormatting: ({ fields, values }) => {
    const editedValuesWithDefaults = { ...values }

    fields.forEach((field) => {
      const { fieldId, defaultValue, fieldType, scale: decimalPlaces } = field || {}
      const value = editedValuesWithDefaults[fieldId] || null
      editedValuesWithDefaults[fieldId] = value

      const valueIsSet = value !== null && value !== undefined && value !== '' // 0 and false are valid values
      const hasADefaultValue = defaultValue !== null && defaultValue !== undefined && defaultValue !== '' // 0 and false are valid values

      // set the default value if there is one
      if (!valueIsSet && hasADefaultValue) {
        editedValuesWithDefaults[fieldId] = get().getDefaultValueForCustomDataField({ field })
      }

      // convert the value to a number if it's a numerical field
      const fieldIsNumerical = fieldType === 'Integer' || fieldType === 'Decimal' || fieldType === 'Money'
      if (fieldIsNumerical && valueIsSet) {
        editedValuesWithDefaults[fieldId] = Number(editedValuesWithDefaults[fieldId])
      }

      // round the value to the correct number of decimal places
      if (fieldIsNumerical && editedValuesWithDefaults[fieldId] !== null) {
        const dp = fieldType === 'Decimal' || fieldType === 'Money' ? decimalPlaces : 0
        editedValuesWithDefaults[fieldId] = roundNumber(editedValuesWithDefaults[fieldId], dp)
      }

      // save the value as a lowercase string if it's a URL or email
      if (fieldType === 'URL' || fieldType === 'Email') {
        if (value && typeof editedValuesWithDefaults[fieldId] === 'string')
          editedValuesWithDefaults[fieldId] = editedValuesWithDefaults[fieldId].toLowerCase()
      }

      // if the value is an empty address, set it to null
      if (fieldType === 'Address' && editedValuesWithDefaults[fieldId] === '||||') {
        editedValuesWithDefaults[fieldId] = null
      }

      // if the value is a date, add the time to the end
      if (fieldType === 'Date' && editedValuesWithDefaults[fieldId] && !value.includes('T00:00:00')) {
        editedValuesWithDefaults[fieldId] += 'T00:00:00'
      }
    })

    return editedValuesWithDefaults
  },
  reset: () => {
    set({ customDataPages: null })
    set(initialState)
  }
}))
